/* eslint-disable react/jsx-pascal-case */
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  useMediaQuery,
  Button,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  useMaterialReactTable,
} from "material-react-table";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  PlayArrow as PlayArrowIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
// import AxiosInstance from './Axios';
// Add this import at the top with other imports
import OpsAlerts from "../OpsAlerts";
import PositionsTable from "../table/PositionsTable";
import { useTheme } from "@mui/material/styles";
import { PositionsDataContext } from "../../context/PositionsDataContext";
import { fetchEzeEcdScrubData, fetchGenevaEcdScrubData, fetchGenevaReportData, postGenevaReportRunRequestByAlias } from "../../services/Axios";
import { CardWrapper, PageHeader, TabNavigation } from "../ui";
import { getPositionsComparisonsColumns } from "../table/columnsConfig/positionsComparisons";
import { getUntaggedPositionsColumns } from "../table/columnsConfig/untaggedPositions";
import { getEcdColumns } from "../table/columnsConfig/ecdColumns";
import { getGenevaReportsColumns } from "../table/columnsConfig/genevaReportsColumns";
import {
  getCommonBodyCellProps,
  getCommonBodyCellPropsTradesPositions,
  getCommonHeadCellProps,
} from "../table/columnsConfig/commonUtils";
import { formatCurrency, formatNumber } from "../../utils/dataCalculations";
import ExportToCSVButton from "../ExportToCsvButton";
import ECDScrub from "../table/ECDScrub";
import { makeGroupedGlobalFilterFn } from "../table/smartFilters";

// Untagged Positions tab: the tab's columns plus the identifying fields
// (SYMBOL, Security_Type, BB_Ticker_Code) it does not display.
const ECD_SEARCH_FILTER = makeGroupedGlobalFilterFn([
  "closing_date",
  "StrategyType",
  "StrategyName",
]);

const untaggedColumnsToExport = [
  "StrategyType",
  "StrategyName",
  "SYMBOL",
  "security_name",
  "Security_Type",
  "BB_Ticker_Code",
  "trading_pnl",
  "current_price",
  "realtime_quantity",
  "net_trading_quantity",
  "sod_quantity",
  "buy_trade_quantity",
  "buy_trade_price",
  "buy_trade_MV",
  "sell_trade_quantity",
  "sell_trade_price",
  "sell_trade_MV",
  "short_trade_quantity",
  "short_trade_price",
  "short_trade_MV",
  "cover_trade_quantity",
  "cover_trade_price",
  "cover_trade_MV",
];

const columnsToExport = [
  "StrategyType",
  "StrategyName",
  "SYMBOL",
  "name",
  "security_name",
  "Security_Type",
  "BB_Ticker_Code",
  "closing_date",
  "LMV",
  "SMV",
];


const OpsPage = () => {
  const theme = useTheme();
  const location = useLocation();
  const { datasets, isLoading: datasetsLoading } = useContext(
    PositionsDataContext,
  );
  const [allData, setAllData] = useState([]);
  const [untaggedPositions, setUntaggedPositions] = useState([]);
  const [missingPricesData, setMissingPricesData] = useState([]);
  const [missingECDData, setMissingECDData] = useState([]);
  const [ecdData, setEcdData] = useState([]);
  const [previousEodPositions, setPreviousEodPositions] = useState([]);
  const [ecdScrubData, setEcdScrubData] = useState({ ezeData: [], genevaData: [] });
  const [missingTickersData, setMissingTickersData] = useState([]);
  const [genevaReportsData, setGenevaReportsData] = useState([]);
  const [genevaReportsLoading, setGenevaReportsLoading] = useState(false);
  const [genevaReportsError, setGenevaReportsError] = useState(null);
  const [genevaReportsGlobalFilter, setGenevaReportsGlobalFilter] = useState(
    () => localStorage.getItem("opsGenevaReportsGlobalFilter") || ""
  );
  const [runningReports, setRunningReports] = useState(new Set());
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  // Handles on the row virtualizers of the tables below, so they can be
  // re-measured after a tab switch (see the effect after handleTabClick).
  const comparisonVirtualizerRef = useRef(null);
  const untaggedVirtualizerRef = useRef(null);
  const tickersVirtualizerRef = useRef(null);

  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("opsReportsPageActiveTab") || "tab1";
  });
  const [alertsCount, setAlertsCount] = useState(0);
  const [alertsData, setOpsAlertsData] = useState({
    stock_ratio_check: [],
    banks_in_GIP: [],
    REG_S_in_GEF: [],
    short_tags: [],
    LP_positions: [],
    last_updated: "",
  });
  const [
    positionsScrollPosition_position_table,
    setPositionsScrollPosition_position_table,
  ] = useState(() => {
    return (
      parseInt(localStorage.getItem("opsPositionsTableScrollPosition"), 10) || 0
    );
  });
  // Add state for positions table filter, sorting, and scroll position
  const [positionsGlobalFilter, setPositionsGlobalFilter] = useState(
    () => localStorage.getItem("opsPositionsTableGlobalFilter") || ""
  );
  const [positionsSorting, setPositionsSorting] = useState(() => {
    const savedSorting = localStorage.getItem("opsPositionsTableSorting");
    return savedSorting
      ? JSON.parse(savedSorting)
      : [{ id: "StrategyName", desc: false }];
  });
  const [positionsScrollPosition, setPositionsScrollPosition] = useState(() => {
    return (
      parseInt(localStorage.getItem("opsPositionsTableScrollPosition"), 10) || 0
    );
  });

  // Add new state for position comparison table scroll position
  const [
    positionComparisonScrollPosition,
    setPositionComparisonScrollPosition,
  ] = useState(() => {
    return (
      parseInt(
        localStorage.getItem("positionComparisonTableScrollPosition"),
        10
      ) || 0
    );
  });

  // Add scroll position state for missing tables
  const [missingPricesScrollPosition, setMissingPricesScrollPosition] =
    useState(() => {
      return (
        parseInt(
          localStorage.getItem("missingPricesTableScrollPosition"),
          10
        ) || 0
      );
    });

  const [untaggedPositionsScrollPosition, setUntaggedPositionsScrollPosition] =
    useState(() => {
      return (
        parseInt(
          localStorage.getItem("untaggedPositionsTableScrollPosition"),
          10
        ) || 0
      );
    });

  const [ecdTableScrollPosition, setEcdTableScrollPosition] = useState(() => {
    return parseInt(localStorage.getItem("ecdTableScrollPosition"), 10) || 0;
  });

  const [missingEcdTableScrollPosition, setMissingEcdTableScrollPosition] =
    useState(() => {
      return (
        parseInt(localStorage.getItem("missingEcdTableScrollPosition"), 10) || 0
      );
    });

  // Add new state variables for tab7
  const [
    previousEodPositionsGlobalFilter,
    setPreviousEodPositionsGlobalFilter,
  ] = useState(
    () => localStorage.getItem("previousEodPositionsTableGlobalFilter") || ""
  );
  const [previousEodPositionsSorting, setPreviousEodPositionsSorting] =
    useState(() => {
      const savedSorting = localStorage.getItem(
        "previousEodPositionsTableSorting"
      );
      return savedSorting
        ? JSON.parse(savedSorting)
        : [{ id: "StrategyName", desc: false }];
    });
  const [
    previousEodPositionsScrollPosition,
    setPreviousEodPositionsScrollPosition,
  ] = useState(() => {
    return (
      parseInt(
        localStorage.getItem("previousEodPositionsTableScrollPosition"),
        10
      ) || 0
    );
  });

  // Add new state variables for tab8
  const [ecdScrubGlobalFilter, setECDScrubGlobalFilter] = useState(
    () => localStorage.getItem("ecdScrubTableGlobalFilter") || "",
  );
  const [ecdScrubSorting, setECDScrubSorting] = useState(() => {
    const savedSorting = localStorage.getItem("ecdScrubTableSorting");
    return savedSorting ? JSON.parse(savedSorting) : [{ id: "eze_ecd_flag", desc: false }];
  });

  // Effects
  useEffect(() => {
    if (datasets?.positions) setAllData(datasets.positions);
    if (datasets?.previousDay) setPreviousEodPositions(datasets.previousDay);
    if (datasets?.opsData) setOpsAlertsData(datasets.opsData);
    if (datasets?.missingTickersData)
      setMissingTickersData(datasets.missingTickersData);
  }, [datasets]);

  // Fetch ECD scrub data on component mount
  useEffect(() => {
    const fetchEcdData = async () => {
      try {
        const [ezeData, genevaData] = await Promise.all([
          fetchEzeEcdScrubData(),
          fetchGenevaEcdScrubData(),
        ]);

        setEcdScrubData({
          ezeData: ezeData || [],
          genevaData: genevaData || []
        });
      } catch (error) {
        console.error("Failed to fetch ECD scrub data:", error);
      }
    };

    fetchEcdData();
  }, []);

  // Fetch Geneva reports data when tab is active
  useEffect(() => {
    const fetchGenevaReports = async () => {
      if (activeTab === "tab10") {
        setGenevaReportsLoading(true);
        setGenevaReportsError(null);
        try {
          const data = await fetchGenevaReportData();
          setGenevaReportsData(data || []);
        } catch (error) {
          console.error("Failed to fetch Geneva reports data:", error);
          setGenevaReportsError(error.message || "Failed to fetch Geneva reports data");
        } finally {
          setGenevaReportsLoading(false);
        }
      }
    };
    
    fetchGenevaReports();
  }, [activeTab]);

  // Handle running Geneva reports
  const handleRunReport = async (reportAlias) => {
    setRunningReports(prev => new Set([...prev, reportAlias]));
    try {
      const result = await postGenevaReportRunRequestByAlias(reportAlias);
      console.log(`Report ${reportAlias} started successfully:`, result);
      // You could add a success notification here
    } catch (error) {
      console.error(`Failed to run report ${reportAlias}:`, error);
      // You could add an error notification here
    } finally {
      setRunningReports(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportAlias);
        return newSet;
      });
    }
  };

  // Geneva Reports table configuration
  const genevaReportsTable = useMaterialReactTable({
    columns: getGenevaReportsColumns(theme, runningReports, handleRunReport),
    data: genevaReportsData,
    enableColumnDragging: false,
    enableSorting: true,
    enableColumnOrdering: false,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: false,
    enableColumnResizing: false,
    enableBottomToolbar: false,
    enablePinning: false,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    columnResizeMode: "onChange",
    enableTopToolbar: false,
    enableColumnActions: false,
    enableRowVirtualization: false,
    sortingDisplay: "none",
    state: {
      globalFilter: genevaReportsGlobalFilter,
    },
    onGlobalFilterChange: setGenevaReportsGlobalFilter,
    muiTableBodyRowProps: {
      sx: {
        opacity: 1,
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px",
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[100],
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.grey[100],
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%",
        tableLayout: "auto",
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px",
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: "calc(100vh - 250px)",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        overflowX: "auto",
      },
    },
  });

  useEffect(() => {
    const container = document.querySelector(
      '[class*="MuiTableContainer-root"]'
    );
    if (container) {
      container.addEventListener("scroll", () => {
        setPositionsScrollPosition_position_table(container.scrollTop);
        localStorage.setItem(
          "opsPositionsTableScrollPosition",
          container.scrollTop
        );
      });
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", () => {});
      }
    };
  }, []);
  const handleAlertsCountChange = (count) => {
    setAlertsCount(count);
  };

  // State initializations with localStorage persistence
  const [savedGlobalFilterPositions, setGlobalFilterPositions] = useState(
    () => localStorage.getItem("opsPositionsTableGlobalFilter") || ""
  );

  const [savedGlobalFilterMissingPrices, setSavedGlobalFilterMissingPrices] =
    useState(() => {
      const saved = localStorage.getItem("missingPricesTableGlobalFilter");
      return saved || "";
    });
  const [globalFilterMissingPrices, setGlobalFilterMissingPrices] = useState(
    savedGlobalFilterMissingPrices
  );

  const [savedGlobalFilterECD, setSavedGlobalFilterECD] = useState(() => {
    return localStorage.getItem("ecdTableGlobalFilter") || "";
  });
  const [globalFilterECD, setGlobalFilterECD] = useState(savedGlobalFilterECD);

  const [
    savedGlobalFilterPositionComparison,
    setSavedGlobalFilterPositionComparison,
  ] = useState(() => {
    const saved = localStorage.getItem("positionComparisonTableGlobalFilter");
    return saved || "";
  });
  const [globalFilterPositionComparison, setGlobalFilterPositionComparison] =
    useState(savedGlobalFilterPositionComparison);

  const [expandedECDRows, setExpandedECDRows] = useState(() => {
    const saved = localStorage.getItem("ecdTableExpandedRows");
    return saved ? JSON.parse(saved) : {};
  });

  const [expandedMissingECDRows, setExpandedMissingECDRows] = useState(() => {
    const saved = localStorage.getItem("missingEcdTableExpandedRows");
    return saved ? JSON.parse(saved) : {};
  });

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    localStorage.setItem("opsReportsPageActiveTab", tab);
  };

  // The positions-based tabs have nothing to show until the first load ends.
  const waitingForData =
    datasetsLoading &&
    allData.length === 0 &&
    ["tab1", "tab2", "tab3", "tab4", "tab5", "tab7"].includes(activeTab);

  // Row-virtualised tables can end up with an empty window when their tab is
  // mounted (the scroll container has no size yet), showing blank rows until
  // something re-renders them, e.g. typing in the filter. Re-measure shortly
  // after a tab becomes active or its content is revealed.
  useEffect(() => {
    const timers = [50, 250, 800].map((ms) =>
      setTimeout(() => {
        [
          comparisonVirtualizerRef,
          untaggedVirtualizerRef,
          tickersVirtualizerRef,
        ].forEach((ref) => ref.current?.measure?.());
      }, ms),
    );
    return () => timers.forEach(clearTimeout);
  }, [activeTab, waitingForData]);

  // Restore active tab on mount (and after refresh). location.key changes on every
  // navigation, including to this same page, so an alert notification's "Open" switches
  // to the Ops Alerts tab even when this page is already showing.
  useEffect(() => {
    const savedTab = localStorage.getItem("opsReportsPageActiveTab");
    if (savedTab) {
      setActiveTab(savedTab);
    }
  }, [location.key]);

  useEffect(() => {
    localStorage.setItem(
      "opsPositionsTableGlobalFilter",
      savedGlobalFilterPositions || ""
    );
  }, [savedGlobalFilterPositions]);

  // Add this helper function near the top of your component, before the positions_table_Columns definition
  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    // Convert both to strings to handle numbers
    const textStr = String(text);
    const searchStr = String(searchTerm);

    // Case insensitive search
    const regex = new RegExp(`(${searchStr})`, "gi");
    const parts = textStr.split(regex);

    return parts?.map((part, index) =>
      part.toLowerCase() === searchStr.toLowerCase() ? (
        <span key={index} style={{ backgroundColor: "rgb(250, 211, 104)" }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  // First, add this data preparation function before the position_comparison_table_Columns definition
  const mergedPositionData = useMemo(() => {
    // First, get all current positions with previous day quantities
    const currentPositionsWithPrevious = allData?.map((currentPosition) => {
      const prevPosition = previousEodPositions?.find(
        (pos) => pos.SYMBOL === currentPosition.SYMBOL
      );

      const currentSodQty = currentPosition.sod_quantity || 0;
      const eodQty = prevPosition ? prevPosition.realtime_quantity : 0;
      const difference = currentSodQty - eodQty;
      const absoluteDifference = Math.abs(difference);

      return {
        ...currentPosition,
        position_type: "Current Position",
        previous_eod_quantity: prevPosition
          ? prevPosition.realtime_quantity
          : null,
        position_difference: difference,
        absolute_difference: absoluteDifference,
      };
    });

    // Then, get all previous positions that don't exist in current positions
    const previousOnlyPositions = previousEodPositions
      ?.filter(
        (prevPosition) =>
          !allData.some((curr) => curr.SYMBOL === prevPosition.SYMBOL)
      )
      ?.map((prevPosition) => {
        const currentPosition = allData?.find(
          (pos) => pos.SYMBOL === prevPosition.SYMBOL
        );

        const prevEodQty = prevPosition.realtime_quantity || 0;
        const currentSodQty = currentPosition
          ? currentPosition.sod_quantity
          : 0; // Set to 0 since position doesn't exist today
        const difference = currentSodQty - prevEodQty;
        const absoluteDifference = Math.abs(difference);

        return {
          ...prevPosition,
          position_type: "Previous Day Qty",
          sod_quantity: currentSodQty,
          previous_eod_quantity: prevEodQty, // Include the previous EOD quantity
          position_difference: difference,
          absolute_difference: absoluteDifference,
        };
      });

    return [...currentPositionsWithPrevious, ...previousOnlyPositions];
  }, [allData, previousEodPositions]);

  useEffect(() => {
    if (allData?.length > 0) {
      // Filter for positions with valid ECD
      const hasECD = allData?.filter(
        (position) =>
          position.closing_date &&
          position.closing_date !== "nan" &&
          position.closing_date !== "" &&
          position.closing_date !== null
      );
      setEcdData(hasECD);

      // Filter for positions with missing prices
      const missingData = allData?.filter(
        (position) =>
          (!position.current_price ||
            position.current_price === 0 ||
            position.current_price === "nan" ||
            isNaN(position.current_price) ||
            !position.market_value ||
            position.market_value === 0 ||
            position.market_value === "nan" ||
            isNaN(position.market_value)) &&
          position.realtime_quantity !== 0
      );
      setMissingPricesData(missingData);

      // Filter for positions with missing StrategyType/StrategyName or containing "Temporary"
      const untaggedPositions = allData?.filter(
        (position) =>
          !position.StrategyType ||
          position.StrategyType === "" ||
          position.StrategyType === "nan" ||
          !position.StrategyName ||
          position.StrategyName === "" ||
          position.StrategyName === "nan" ||
          (position.StrategyName && position.StrategyName.includes("Temporary"))
      );
      setUntaggedPositions(untaggedPositions);

      // Filter for positions with missing ECD
      const missingECD = allData?.filter(
        (position) =>
          !position.closing_date ||
          position.closing_date === "nan" ||
          position.closing_date === "" ||
          position.closing_date === null
      );
      setMissingECDData(missingECD);
    }
  }, [allData]);

  const enrichedEcdData = useMemo(() => {
    if (!ecdData?.length) return [];
    const lmvByDate = {};
    ecdData.forEach((row) => {
      const d = row.closing_date;
      if (!d) return;
      lmvByDate[d] = (lmvByDate[d] || 0) + (Number(row.LMV) || 0);
    });
    const sortedDates = Object.keys(lmvByDate).sort();
    const cumulativeMap = {};
    let running = 0;
    sortedDates.forEach((d) => {
      running += lmvByDate[d];
      cumulativeMap[d] = running;
    });
    return ecdData.map((row) => ({
      ...row,
      cumulative_lmv: cumulativeMap[row.closing_date] || 0,
    }));
  }, [ecdData]);

  const position_comparison_table = useMaterialReactTable({
    columns: getPositionsComparisonsColumns(theme),
    data: mergedPositionData,
    enableColumnDragging: false,
    enableSorting: true,
    enableColumnOrdering: false,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true, // Ensures footer sticks to the bottom
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    columnResizeMode: "onChange",
    enableTopToolbar: false,
    enableColumnActions: false,
    enableRowVirtualization: true,
    rowVirtualizerInstanceRef: comparisonVirtualizerRef,
    enableDensityToggle: false,
    virtualizerProps: { overscan: 10 },
    initialState: {
      density: "compact",
      sorting: [{ id: "absolute_difference", desc: true }], // Sort by absolute difference in descending order
      showColumnFilters: false,
      showGlobalFilter: true,
      globalFilter: savedGlobalFilterPositionComparison,
      columnPinning: isDesktop
        ? { left: [] } // No columns pinned by default on desktop
        : {}, // No columns pinned in mobile mode
    },
    state: {
      globalFilter: globalFilterPositionComparison,
    },
    onGlobalFilterChange: setGlobalFilterPositionComparison,
    displayColumnDefOptions: {
      "mrt-row-expand": {
        Cell: ({ row, cell }) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${row.depth * 2}rem`,
              cursor: "pointer",
            }}
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? (
              <RemoveIcon sx={{ fontSize: { xs: "14px", sm: "16px" } }} /> // Responsive icon size
            ) : (
              <AddIcon sx={{ fontSize: { xs: "14px", sm: "16px" } }} />
            )}
            {row.getIsGrouped() && (
              <span
                style={{
                  marginLeft: "8px",
                  fontWeight: "bold",
                  textAlign: "left",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" }, // Responsive font size
                }}
              >
                {row.groupingValue}
              </span>
            )}
          </div>
        ),
      },
    },
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        // Default background for even rows
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400], // Hover effect for all rows
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "calc(100vh - 200px)", // Adjusted for mobile
          sm: "calc(100vh - 130px)", // Default for desktop
        },
        marginBottom: "0px",
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });
  const untaggedPositions_table = useMaterialReactTable({
    columns: getUntaggedPositionsColumns(theme),
    data: untaggedPositions,
    enableColumnDragging: false,
    enableSorting: true,
    enableColumnOrdering: false,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true, // Ensures footer sticks to the bottom
    enableColumnResizing: true, // Changed to true for responsive column sizing
    enableBottomToolbar: false,
    enablePinning: true,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    columnResizeMode: "onChange",
    enableTopToolbar: false,
    enableColumnActions: false,
    enableRowVirtualization: true,
    rowVirtualizerInstanceRef: untaggedVirtualizerRef,
    displayColumnDefOptions: {
      "mrt-row-numbers": {
        enableColumnOrdering: false,
        enableSorting: false,
      },
    },
    sortingDisplay: "none",
    initialState: {
      sorting: [
        { id: "StrategyName", desc: false },
        { id: "security_name", desc: false },
      ],
      density: "compact",
      columnPinning: isDesktop
        ? { left: ["StrategyName"] } // Pin StrategyName on desktop
        : {}, // No columns pinned in mobile mode
    },
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        // Default background for even rows
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400], // Hover effect for all rows
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "calc(100vh - 200px)", // Adjusted for mobile
          sm: "calc(100vh - 130px)", // Default for desktop
        },
        marginBottom: "0px",
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });
  const getMissingTickersColumns = () => {
    return [
      {
        header: "Symbol",
        accessorKey: "SYMBOL",
        size: 200,
      },
      {
        header: "Security Name",
        accessorKey: "Name",
        size: 200,
      },
      {
        header: "Bloomberg Ticker",
        accessorKey: "BB_Ticker_Code",
        size: 300,
      },
    ];
  };
  const missingTickers_table = useMaterialReactTable({
    columns: getMissingTickersColumns(),
    data: missingTickersData,
    enableColumnDragging: false,
    enableSorting: true,
    enableColumnOrdering: false,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true, // Ensures footer sticks to the bottom
    enableColumnResizing: true, // Changed to true for responsive column sizing
    enableBottomToolbar: false,
    enablePinning: true,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    columnResizeMode: "onChange",
    enableTopToolbar: false,
    enableColumnActions: false,
    enableRowVirtualization: true,
    rowVirtualizerInstanceRef: tickersVirtualizerRef,
    displayColumnDefOptions: {
      "mrt-row-numbers": {
        enableColumnOrdering: false,
        enableSorting: false,
      },
    },
    sortingDisplay: "none",
    initialState: {
      sorting: [{ id: "BB_Ticker_Code", desc: false }],
      density: "compact",
    },
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        // Default background for even rows
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400], // Hover effect for all rows
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "calc(100vh - 200px)", // Adjusted for mobile
          sm: "calc(100vh - 130px)", // Default for desktop
        },
        marginBottom: "0px",
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });
  const ECD_table = useMaterialReactTable({
    columns: getEcdColumns(theme),
    data: enrichedEcdData,
    enableColumnDragging: false,
    enableGrouping: true,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    groupedColumnMode: "remove",
    enableTopToolbar: false,
    enableColumnActions: false,
    enableGlobalFilter: true,
    // groupedColumnMode "remove" drops the grouped columns from the columns
    // the search box covers, so searching "merger arb" found nothing. This
    // filter also checks the grouped fields on each row, ignoring case and
    // punctuation.
    filterFns: { ecdSearch: ECD_SEARCH_FILTER },
    globalFilterFn: "ecdSearch",
    layoutMode: "grid",
    initialState: {
      grouping: ["closing_date", "StrategyType", "StrategyName"],
      sorting: [{ id: "closing_date", desc: false }],
      density: "compact",
      columnVisibility: {
        created_at: false,
        updated_at: false,
        last_closing_price: false,
      },
      columnPinning: isDesktop
        ? {
            left: [
              "mrt-row-expand",
              "closing_date",
              "StrategyType",
              "StrategyName",
            ],
          }
        : {},
      expanded: expandedECDRows,
      showColumnFilters: false,
      showGlobalFilter: true,
      globalFilter: savedGlobalFilterECD,
    },
    state: {
      expanded: expandedECDRows,
      globalFilter: globalFilterECD,
    },
    onExpandedChange: (updater) => {
      const newExpanded =
        typeof updater === "function" ? updater(expandedECDRows) : updater;
      setExpandedECDRows(newExpanded);
      localStorage.setItem("ecdTableExpandedRows", JSON.stringify(newExpanded));
    },
    onGlobalFilterChange: setGlobalFilterECD,
    displayColumnDefOptions: {
      "mrt-row-expand": {
        header: "Group",
        size: 120,
        grow: true,
        Cell: ({ row }) => {
          const isGrouped = row.getIsGrouped();
          const depth = row.depth;
          const subRows = row.subRows;
          const canExpand = isGrouped && subRows && subRows.length > 0;
          const leafCount = row.getLeafRows?.()?.length;
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                paddingLeft: `${depth * 1}rem`,
                cursor: canExpand ? "pointer" : "default",
                userSelect: "none",
              }}
              onClick={canExpand ? row.getToggleExpandedHandler() : undefined}
            >
              {canExpand ? (
                row.getIsExpanded() ? (
                  <ExpandMoreIcon
                    sx={{
                      fontSize: 16,
                      color: theme.palette.text.secondary,
                    }}
                  />
                ) : (
                  <ChevronRightIcon
                    sx={{
                      fontSize: 16,
                      color: theme.palette.text.secondary,
                    }}
                  />
                )
              ) : (
                <span style={{ width: 16, display: "inline-block" }} />
              )}
              {isGrouped && (
                <span
                  style={{
                    marginLeft: "3px",
                    fontWeight: depth === 0 ? 700 : 600,
                    fontSize: depth === 0 ? "0.78rem" : "0.73rem",
                    color: theme.palette.text.primary,
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.groupingValue}
                  {leafCount > 0 && (
                    <span
                      style={{
                        marginLeft: "4px",
                        fontSize: "0.63rem",
                        fontWeight: 500,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      ({leafCount})
                    </span>
                  )}
                </span>
              )}
            </div>
          );
        },
      },
    },
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px",
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400],
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "calc(100vh - 200px)",
          sm: "calc(100vh - 130px)",
        },
        marginBottom: "0px",
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        overflowY: "auto",
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%",
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px",
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100,
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5),
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });
  const missing_ECD_table = useMaterialReactTable({
    columns: getEcdColumns(theme),
    data: missingECDData,
    enableColumnDragging: false,
    enableGrouping: true,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    groupedColumnMode: "remove",
    enableTopToolbar: false,
    enableColumnActions: false,
    layoutMode: "grid",
    initialState: {
      grouping: ["StrategyType", "StrategyName"],
      sorting: [{ id: "StrategyType", desc: false }],
      density: "compact",
      columnVisibility: {
        created_at: false,
        updated_at: false,
        cumulative_lmv: false,
        closing_date: false,
      },
      columnPinning: isDesktop
        ? { left: ["mrt-row-expand"] }
        : {},
      expanded: expandedMissingECDRows,
    },
    state: {
      expanded: expandedMissingECDRows,
    },
    onExpandedChange: (updater) => {
      const newExpanded =
        typeof updater === "function"
          ? updater(expandedMissingECDRows)
          : updater;
      setExpandedMissingECDRows(newExpanded);
      localStorage.setItem(
        "missingEcdTableExpandedRows",
        JSON.stringify(newExpanded),
      );
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        header: "Group",
        size: 200,
        grow: true,
        muiTableBodyCellProps: { sx: { overflow: "visible" } },
        Cell: ({ row }) => {
          const isGrouped = row.getIsGrouped();
          const depth = row.depth;
          const subRows = row.subRows;
          const canExpand = isGrouped && subRows && subRows.length > 0;
          const leafCount = row.getLeafRows?.()?.length;
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                paddingLeft: `${depth * 1}rem`,
                cursor: canExpand ? "pointer" : "default",
                userSelect: "none",
              }}
              onClick={canExpand ? row.getToggleExpandedHandler() : undefined}
            >
              {canExpand ? (
                row.getIsExpanded() ? (
                  <ExpandMoreIcon
                    sx={{
                      fontSize: 16,
                      color: theme.palette.text.secondary,
                    }}
                  />
                ) : (
                  <ChevronRightIcon
                    sx={{
                      fontSize: 16,
                      color: theme.palette.text.secondary,
                    }}
                  />
                )
              ) : (
                <span style={{ width: 16, display: "inline-block" }} />
              )}
              {isGrouped && (
                <span
                  style={{
                    marginLeft: "3px",
                    fontWeight: depth === 0 ? 700 : 600,
                    fontSize: depth === 0 ? "0.78rem" : "0.73rem",
                    color: theme.palette.text.primary,
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.groupingValue}
                  {leafCount > 0 && (
                    <span
                      style={{
                        marginLeft: "4px",
                        fontSize: "0.63rem",
                        fontWeight: 500,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      ({leafCount})
                    </span>
                  )}
                </span>
              )}
            </div>
          );
        },
      },
    },
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        // Default background for even rows
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400], // Hover effect for all rows
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "calc(100vh - 200px)", // Adjusted for mobile
          sm: "calc(100vh - 130px)", // Default for desktop
        },
        marginBottom: "0px",
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });

  // Add useEffect for positions table filter persistence
  useEffect(() => {
    localStorage.setItem(
      "opsPositionsTableGlobalFilter",
      positionsGlobalFilter || ""
    );
  }, [positionsGlobalFilter]);

  // Add filter persistence for Geneva Reports table
  useEffect(() => {
    localStorage.setItem(
      "opsGenevaReportsGlobalFilter",
      genevaReportsGlobalFilter || ""
    );
  }, [genevaReportsGlobalFilter]);

  // Add filter persistence for missing prices table
  useEffect(() => {
    localStorage.setItem(
      "missingPricesTableGlobalFilter",
      globalFilterMissingPrices || ""
    );
  }, [globalFilterMissingPrices]);

  // Add filter persistence for ECD table
  useEffect(() => {
    localStorage.setItem("ecdTableGlobalFilter", globalFilterECD || "");
  }, [globalFilterECD]);

  // Add filter persistence for ECD scrub table
  useEffect(() => {
    localStorage.setItem("ecdScrubTableGlobalFilter", ecdScrubGlobalFilter || "");
  }, [ecdScrubGlobalFilter]);

  // Add sorting persistence for ECD scrub table
  useEffect(() => {
    localStorage.setItem("ecdScrubTableSorting", JSON.stringify(ecdScrubSorting));
  }, [ecdScrubSorting]);

  // Add filter persistence for position comparison table
  useEffect(() => {
    localStorage.setItem(
      "positionComparisonTableGlobalFilter",
      globalFilterPositionComparison || ""
    );
  }, [globalFilterPositionComparison]);

  // Add useEffect for positions table sorting persistence
  useEffect(() => {
    localStorage.setItem(
      "opsPositionsTableSorting",
      JSON.stringify(positionsSorting)
    );
  }, [positionsSorting]);

  // Add useEffect to restore scroll position when component mounts
  useEffect(() => {
    const savedScrollPosition = localStorage.getItem(
      "opsPositionsTableScrollPosition"
    );
    if (savedScrollPosition) {
      setPositionsScrollPosition(parseInt(savedScrollPosition));
    }
  }, []);

  // Add useEffect to restore scroll position when Positions tab is selected and table is rendered
  useEffect(() => {
    if (activeTab === "Positions") {
      // Use setTimeout to ensure the table is rendered
      const timer = setTimeout(() => {
        const container = document.querySelector(".MuiTableContainer-root");
        if (container) {
          container.scrollTop = positionsScrollPosition;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeTab, positionsScrollPosition]);

  // Add useEffect for positions table scroll position persistence
  useEffect(() => {
    const handleScroll = () => {
      const container = document.querySelector(".MuiTableContainer-root");
      if (container) {
        setPositionsScrollPosition(container.scrollTop);
        localStorage.setItem(
          "opsPositionsTableScrollPosition",
          container.scrollTop
        );
      }
    };

    const container = document.querySelector(".MuiTableContainer-root");
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  // Add useEffect to restore positions table filter on component mount
  useEffect(() => {
    const savedFilter = localStorage.getItem("opsPositionsTableGlobalFilter");
    if (savedFilter) {
      setPositionsGlobalFilter(savedFilter);
    }
    
    const savedGenevaFilter = localStorage.getItem("opsGenevaReportsGlobalFilter");
    if (savedGenevaFilter) {
      setGenevaReportsGlobalFilter(savedGenevaFilter);
    }
  }, []);

  const handleExportECD = () => {
    // Define the columns we want to export
    const columnsToExport = [
      "StrategyType",
      "StrategyName",
      "SYMBOL",
      "name",
      "security_name",
      "Security_Type",
      "BB_Ticker_Code",
      "closing_date",
      "LMV",
      "SMV",
    ];

    // Create CSV header
    const csvHeader = columnsToExport.join(",");

    // Create CSV rows
    const csvRows = ecdData?.map((row) => {
      return columnsToExport
        ?.map((column) => {
          const value = row[column];
          // Handle null/undefined values
          if (value === null || value === undefined) return "";
          // Handle string values that contain commas
          if (typeof value === "string" && value.includes(",")) {
            return `"${value}"`;
          }
          return value;
        })
        .join(",");
    });

    // Combine header and rows
    const csvContent = [csvHeader, ...csvRows].join("\n");

    // Create and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `ecd_data_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Add scroll position effects
  useEffect(() => {
    const handleScroll = () => {
      if (activeTab === "tab1") {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]'
        );
        if (container) {
          localStorage.setItem(
            "opsPositionsTableScrollPosition",
            container.scrollTop
          );
          setPositionsScrollPosition_position_table(container.scrollTop);
        }
      }
    };

    const container = document.querySelector(
      '[class*="MuiTableContainer-root"]'
    );
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [activeTab]);

  useEffect(() => {
    const restoreScrollPosition = () => {
      if (activeTab === "tab1") {
        const storedPosition =
          parseInt(
            localStorage.getItem("opsPositionsTableScrollPosition"),
            10
          ) || 0;
        setTimeout(() => {
          const container = document.querySelector(
            '[class*="MuiTableContainer-root"]'
          );
          if (container) {
            container.scrollTop = storedPosition;
          }
        }, 100);
      }
    };

    restoreScrollPosition();
  }, [activeTab]);

  // Add useEffect for position comparison table scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (activeTab === "tab5") {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]'
        );
        if (container) {
          localStorage.setItem(
            "positionComparisonTableScrollPosition",
            container.scrollTop
          );
          setPositionComparisonScrollPosition(container.scrollTop);
        }
      }
    };

    const container = document.querySelector(
      '[class*="MuiTableContainer-root"]'
    );
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [activeTab]);

  // Add useEffect to restore position comparison table scroll position
  useEffect(() => {
    const restoreScrollPosition = () => {
      if (activeTab === "tab5") {
        const storedPosition =
          parseInt(
            localStorage.getItem("positionComparisonTableScrollPosition"),
            10
          ) || 0;
        setTimeout(() => {
          const container = document.querySelector(
            '[class*="MuiTableContainer-root"]'
          );
          if (container) {
            container.scrollTop = storedPosition;
          }
        }, 100);
      }
    };

    restoreScrollPosition();
  }, [activeTab]);

  // Add useEffect for previousEodPositions table filter persistence
  useEffect(() => {
    localStorage.setItem(
      "previousEodPositionsTableGlobalFilter",
      previousEodPositionsGlobalFilter || ""
    );
  }, [previousEodPositionsGlobalFilter]);

  // Add useEffect for previousEodPositions table sorting persistence
  useEffect(() => {
    localStorage.setItem(
      "previousEodPositionsTableSorting",
      JSON.stringify(previousEodPositionsSorting)
    );
  }, [previousEodPositionsSorting]);

  // Modify the useEffect for previousEodPositions table scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (activeTab === "tab7") {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]'
        );
        if (container) {
          const scrollPosition = container.scrollTop;
          localStorage.setItem(
            "previousEodPositionsTableScrollPosition",
            scrollPosition.toString()
          );
          setPreviousEodPositionsScrollPosition(scrollPosition);
        }
      }
    };

    const container = document.querySelector(
      '[class*="MuiTableContainer-root"]'
    );
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [activeTab]);

  // Modify the useEffect to restore previousEodPositions table scroll position
  useEffect(() => {
    if (activeTab === "tab7") {
      const storedPosition =
        parseInt(
          localStorage.getItem("previousEodPositionsTableScrollPosition"),
          10
        ) || 0;
      // Use requestAnimationFrame to ensure the table is rendered
      requestAnimationFrame(() => {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]'
        );
        if (container) {
          container.scrollTop = storedPosition;
        }
      });
    }
  }, [activeTab, previousEodPositions]); // Add previousEodPositions as dependency

  // Add a new useEffect to handle initial scroll position
  useEffect(() => {
    if (activeTab === "tab7" && previousEodPositions?.length > 0) {
      const storedPosition =
        parseInt(
          localStorage.getItem("previousEodPositionsTableScrollPosition"),
          10
        ) || 0;
      const timer = setTimeout(() => {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]'
        );
        if (container) {
          container.scrollTop = storedPosition;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeTab, previousEodPositions]);

  // Add scroll position effects for Missing Prices table (tab2)
  useEffect(() => {
    const handleScroll = () => {
      if (activeTab === "tab2") {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]'
        );
        if (container) {
          const scrollPosition = container.scrollTop;
          localStorage.setItem(
            "missingPricesTableScrollPosition",
            scrollPosition.toString()
          );
          setMissingPricesScrollPosition(scrollPosition);
        }
      }
    };

    const container = document.querySelector(
      '[class*="MuiTableContainer-root"]'
    );
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [activeTab]);

  // Add scroll position effects for Untagged Positions table (tab3)
  useEffect(() => {
    const handleScroll = () => {
      if (activeTab === "tab3") {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]'
        );
        if (container) {
          const scrollPosition = container.scrollTop;
          localStorage.setItem(
            "untaggedPositionsTableScrollPosition",
            scrollPosition.toString()
          );
          setUntaggedPositionsScrollPosition(scrollPosition);
        }
      }
    };

    const container = document.querySelector(
      '[class*="MuiTableContainer-root"]'
    );
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [activeTab]);

  // Add scroll position effects for ECD tables (tab4)
  useEffect(() => {
    const handleScroll = () => {
      if (activeTab === "tab4") {
        const containers = document.querySelectorAll(
          '[class*="MuiTableContainer-root"]'
        );
        if (containers?.length >= 2) {
          // First container is ECD table
          const ecdContainer = containers[0];
          const ecdScrollPosition = ecdContainer.scrollTop;
          localStorage.setItem(
            "ecdTableScrollPosition",
            ecdScrollPosition.toString()
          );
          setEcdTableScrollPosition(ecdScrollPosition);

          // Second container is Missing ECD table
          const missingEcdContainer = containers[1];
          const missingEcdScrollPosition = missingEcdContainer.scrollTop;
          localStorage.setItem(
            "missingEcdTableScrollPosition",
            missingEcdScrollPosition.toString()
          );
          setMissingEcdTableScrollPosition(missingEcdScrollPosition);
        }
      }
    };

    const containers = document.querySelectorAll(
      '[class*="MuiTableContainer-root"]'
    );
    containers.forEach((container) => {
      container.addEventListener("scroll", handleScroll);
    });

    return () => {
      containers.forEach((container) => {
        container.removeEventListener("scroll", handleScroll);
      });
    };
  }, [activeTab]);

  // Restore scroll positions when tabs are activated
  useEffect(() => {
    if (activeTab === "tab2") {
      const timer = setTimeout(() => {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]'
        );
        if (container) {
          container.scrollTop = missingPricesScrollPosition;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeTab, missingPricesScrollPosition]);

  useEffect(() => {
    if (activeTab === "tab3") {
      const timer = setTimeout(() => {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]'
        );
        if (container) {
          container.scrollTop = untaggedPositionsScrollPosition;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeTab, untaggedPositionsScrollPosition]);

  useEffect(() => {
    if (activeTab === "tab4") {
      const timer = setTimeout(() => {
        const containers = document.querySelectorAll(
          '[class*="MuiTableContainer-root"]'
        );
        if (containers?.length >= 2) {
          containers[0].scrollTop = ecdTableScrollPosition;
          containers[1].scrollTop = missingEcdTableScrollPosition;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeTab, ecdTableScrollPosition, missingEcdTableScrollPosition]);

  // Tab configuration
  const tabs = [
    { value: "tab1", label: "Positions" },
    { value: "tab2", label: "Missing Prices" },
    { value: "tab3", label: "Untagged Positions" },
    { value: "tab4", label: "ECD Waterfall" },
    { value: "tab5", label: "Day over Day Comparison" },
    { value: "tab6", label: "Ops Alerts" },
    { value: "tab7", label: "Previous Day EOD Positions" },
    { value: "tab8", label: "ECD Scrub" },
    { value: "tab9", label: "Missing Tickers" },
    { value: "tab10", label: "Geneva Reports" },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      {/* Tab Navigation */}
      <CardWrapper sx={{ mb: 3 }}>
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabClick}
        />
      </CardWrapper>
      {/* Tab content */}
      {waitingForData && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            py: 10,
            color: "text.secondary",
          }}
        >
          <CircularProgress />
          <Box>Loading positions...</Box>
        </Box>
      )}
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          display: waitingForData ? "none" : "block",
        }}
      >
        {activeTab === "tab1" && (
          <PositionsTable
            data={allData}
            title="All Positions"
            globalFilter={savedGlobalFilterPositions}
            setGlobalFilter={setGlobalFilterPositions}
            sorting={positionsSorting}
            setSorting={setPositionsSorting}
            scrollPosition={positionsScrollPosition_position_table}
          />
        )}
        {activeTab === "tab2" && (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
            }}
          >
            <PositionsTable
              data={missingPricesData}
              title="Positions Missing Prices"
              globalFilter={savedGlobalFilterPositions}
              setGlobalFilter={setGlobalFilterPositions}
              sorting={positionsSorting}
              setSorting={setPositionsSorting}
              scrollPosition={positionsScrollPosition_position_table}
            />
          </div>
        )}
        {activeTab === "tab3" && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
              borderRadius: 0,
              boxShadow: theme.shadows[1],
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
              <ExportToCSVButton
                columns={untaggedColumnsToExport}
                theme={theme}
                globalFilter=""
                formatCurrency={formatCurrency}
                formatNumber={formatNumber}
                data={untaggedPositions}
                filename={"Untagged Positions"}
              />
            </Box>
            <MaterialReactTable table={untaggedPositions_table} />
          </Box>
        )}
        {activeTab === "tab4" && (
          <Box
            sx={{
              width: "100%",
              overflow: "hidden",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: "16px",
              padding: "8px",
              height: "calc(100vh - 110px)",
            }}
          >
            {/* Left Section - ECD Waterfall */}
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                backgroundColor: theme.palette.background.paper,
                borderRadius: 0,
                boxShadow: theme.shadows[1],
                flex: { xs: "1 1 auto", md: "6 1 0%" },
                minWidth: 0,
              }}
            >
              <PageHeader
                sx={{
                  px: "20px",
                  justifyContent: "flex-start",
                  alignItems: "end",
                }}
                title={"ECD Waterfall"}
                action={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center", // vertically center items
                      justifyContent: "flex-start", // left align
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    {/* Search Field */}
                    <Box sx={{ maxWidth: "280px", flexShrink: 0 }}>
                      <MRT_GlobalFilterTextField
                        table={ECD_table}
                        sx={{
                          width: "100%",
                          "& .MuiInputBase-root": {
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: theme.shape.borderRadius,
                            border: `1px solid ${theme.palette.divider}`,
                            fontSize: "0.875rem",
                            "&:hover": {
                              backgroundColor: theme.palette.background.paper,
                              borderColor: theme.palette.primary.dark,
                            },
                            "&.Mui-focused": {
                              backgroundColor: theme.palette.background.paper,
                              borderColor: theme.palette.primary.dark,
                              boxShadow: `0 0 0 2px ${theme.palette.primary.dark}20`,
                            },
                          },
                          "& .MuiInputBase-input": {
                            padding: theme.spacing(0.75, 1.25),
                            color: theme.palette.text.primary,
                            "&::placeholder": {
                              color: theme.palette.text.secondary,
                              opacity: 1,
                            },
                          },
                        }}
                      />
                    </Box>
                    <ExportToCSVButton
                      columns={columnsToExport}
                      theme={theme}
                      globalFilter=""
                      formatCurrency={formatCurrency}
                      formatNumber={formatNumber}
                      data={enrichedEcdData}
                      filename={"Ecd Data"}
                    />
                  </Box>
                }
              />
              <Box
                style={{
                  flex: 1,
                  overflow: "hidden",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              >
                <MaterialReactTable table={ECD_table} />
              </Box>
            </Box>

            {/* Right Section - Missing/Excluded Positions */}
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
                overflowX: "auto",
                backgroundColor: theme.palette.background.paper,
                borderRadius: 0,
                boxShadow: theme.shadows[1],
                flex: { xs: "1 1 auto", md: "4 1 0%" },
                minWidth: 0,
              }}
            >
              <PageHeader
                sx={{
                  px: "20px",
                  justifyContent: "flex-start",
                  alignItems: "end",
                }}
                title={"Missing ECD/Excluded Positions"}
              />
              <Box
                style={{
                  flex: 1,
                  overflow: "hidden",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              >
                <MaterialReactTable table={missing_ECD_table} />
              </Box>
            </Box>
          </Box>
        )}
        {activeTab === "tab5" && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
              borderRadius: 0,
              boxShadow: theme.shadows[1],
            }}
          >
            <PageHeader
              sx={{
                px: "20px",
                justifyContent: "flex-start",
                alignItems: "end",
              }}
              title={"Day over Day Comparison"}
              action={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center", // vertically center items
                    justifyContent: "flex-start", // left align
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  {/* Search Field */}
                  <Box sx={{ maxWidth: "280px", flexShrink: 0 }}>
                    <MRT_GlobalFilterTextField
                      table={position_comparison_table}
                      sx={{
                        width: "100%",
                        "& .MuiInputBase-root": {
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: theme.shape.borderRadius,
                          border: `1px solid ${theme.palette.divider}`,
                          fontSize: "0.875rem",
                          "&:hover": {
                            backgroundColor: theme.palette.background.paper,
                            borderColor: theme.palette.primary.dark,
                          },
                          "&.Mui-focused": {
                            backgroundColor: theme.palette.background.paper,
                            borderColor: theme.palette.primary.dark,
                            boxShadow: `0 0 0 2px ${theme.palette.primary.dark}20`,
                          },
                        },
                        "& .MuiInputBase-input": {
                          padding: theme.spacing(0.75, 1.25),
                          color: theme.palette.text.primary,
                          "&::placeholder": {
                            color: theme.palette.text.secondary,
                            opacity: 1,
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>
              }
            />
            {/* Lazy load the table only when tab is active */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                backgroundColor: "#f5f5f5",
                padding: "8px",
              }}
            >
              <MaterialReactTable
                table={position_comparison_table}
                muiTableContainerProps={{
                  sx: {
                    maxHeight: "calc(100vh - 400px)",
                    overflow: "auto",
                  },
                }}
                muiTablePaperProps={{
                  sx: {
                    position: "relative",
                  },
                }}
              />
            </Box>
          </Box>
        )}
        {activeTab === "tab6" && (
          <OpsAlerts
            onAlertsCountChange={handleAlertsCountChange}
            alertsData={alertsData}
          />
        )}
        {activeTab === "tab7" && (
          <PositionsTable
            data={previousEodPositions}
            title="Previous Day EOD Positions"
            globalFilter={previousEodPositionsGlobalFilter}
            setGlobalFilter={setPreviousEodPositionsGlobalFilter}
            sorting={previousEodPositionsSorting}
            setSorting={setPreviousEodPositionsSorting}
            scrollPosition={previousEodPositionsScrollPosition}
          />
        )}
        {activeTab === "tab8" && (
          <ECDScrub
            title="ECD Scrub"
            // Remove the data prop - let ECDScrub fetch its own data
            ezeGlobalFilter={ecdScrubGlobalFilter}
            setEzeGlobalFilter={setECDScrubGlobalFilter}
            ezeSorting={ecdScrubSorting}
            setEzeSorting={setECDScrubSorting}
            genevaGlobalFilter={ecdScrubGlobalFilter}
            setGenevaGlobalFilter={setECDScrubGlobalFilter}
            genevaSorting={ecdScrubSorting}
            setGenevaSorting={setECDScrubSorting}
          />
        )}
        {activeTab === "tab9" && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
              borderRadius: 0,
              boxShadow: theme.shadows[1],
            }}
          >
            <PageHeader
              sx={{
                px: "20px",
                justifyContent: "flex-start",
                alignItems: "end",
              }}
              title={"Missing Tickers"}
              action={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <ExportToCSVButton
                    columns={["SYMBOL", "Name", "BB_Ticker_Code"]}
                    theme={theme}
                    globalFilter=""
                    formatCurrency={formatCurrency}
                    formatNumber={formatNumber}
                    data={missingTickersData}
                    filename={"Missing Tickers"}
                  />
                </Box>
              }
            />
            <Box
              style={{
                flex: 1,
                overflow: "hidden",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            >
              <MaterialReactTable table={missingTickers_table} />
            </Box>
          </Box>
        )}
        {activeTab === "tab10" && (
          <Box
            sx={{
              width: "100%",
              minHeight: "calc(100vh - 200px)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: theme.palette.background.default,
            }}
          >
            <PageHeader
              sx={{
                px: 3,
                py: 2,
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: `1px solid ${theme.palette.divider}`,
                flexShrink: 0,
                backgroundColor: theme.palette.background.paper,
              }}
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <span>Geneva Reports</span>
                  <Box sx={{ maxWidth: "280px", flexShrink: 0 }}>
                    <input
                      type="text"
                      placeholder="Search reports..."
                      value={genevaReportsGlobalFilter}
                      onChange={(e) => setGenevaReportsGlobalFilter(e.target.value)}
                      style={{
                        width: "100%",
                        height: "32px",
                        fontSize: "0.75rem",
                        padding: "6px 12px",
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: "4px",
                        outline: "none",
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                      }}
                    />
                  </Box>
                </Box>
              }
              action={
                <ExportToCSVButton
                  columns={["ReportAlias", "ReportParameters"]}
                  theme={theme}
                  globalFilter=""
                  formatCurrency={formatCurrency}
                  formatNumber={formatNumber}
                  data={genevaReportsData}
                  filename={"Geneva Reports"}
                />
              }
            />
            <Box
              sx={{
                flex: 1,
                width: "100%",
                overflow: "hidden",
                p: 0,
                minHeight: 0,
                backgroundColor: theme.palette.background.default,
              }}
            >
              {genevaReportsLoading ? (
                <Box 
                  sx={{ 
                    textAlign: "center", 
                    padding: "40px",
                    color: theme.palette.text.secondary,
                    fontSize: "0.75rem",
                  }}
                >
                  Loading Geneva reports...
                </Box>
              ) : genevaReportsError ? (
                <Box 
                  sx={{ 
                    textAlign: "center", 
                    padding: "40px", 
                    color: theme.palette.error.main,
                    fontSize: "0.75rem",
                  }}
                >
                  Error: {genevaReportsError}
                </Box>
              ) : genevaReportsData.length === 0 ? (
                <Box 
                  sx={{ 
                    textAlign: "center", 
                    padding: "40px",
                    color: theme.palette.text.secondary,
                    fontSize: "0.75rem",
                  }}
                >
                  No Geneva reports data available
                </Box>
              ) : (
                <MaterialReactTable table={genevaReportsTable} />
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OpsPage;
